import { Component, OnInit, OnDestroy, ViewChild, ElementRef, NgZone } from '@angular/core';
import { ActivatedRoute, NavigationEnd, RouterEvent, Scroll } from '@angular/router';

import { DropzoneComponent, DropzoneConfigInterface } from "ngx-dropzone-wrapper";
import { DropzoneService } from "../../services/dropzone.service";

// ionic
import { ModalController } from '@ionic/angular';
import { ActionSheetController } from '@ionic/angular';
import { Platform } from "@ionic/angular";

// ionic native
import { Camera } from '@awesome-cordova-plugins/camera/ngx';

// rxjs
import { Subject, Subscription } from "rxjs";
import { debounceTime, take, takeUntil } from "rxjs/operators";

// components
import { AttendeeDetailComponent } from "../../components/attendee-detail/attendee-detail.component";
import { EditMessageComponent } from "../../components/edit-message/edit-message.component";
import { MessageFileFormComponent } from "../../components/message-file-form/message-file-form.component";
import { ChagGroupsFormComponent } from "../../components/chat-groups-form/chat-groups-form.component";
import { ChimeComponent } from '../../components/chime/chime.component';

// services
import { BroadcastService } from "../../services/broadcast.service";
import { ConversationService } from "../../services/conversation.service";
import { ChimeService } from "../../services/chime.service";
import { ChatGroupsService } from "../../services/chat-groups.service";
import { MarketplaceService } from "../../services/marketplace.service";
import { Helper } from "../../services/helper";

// models
import { Appointment } from "../../models/appointment";
import { Event } from "../../models/event";
import { Broadcast } from "../../models/broadcast";
import { Conversation } from "../../models/conversation"
import { ConversationMessage } from "../../models/conversation-message";
import { ChatGroup } from "../../models/chat-group";
import { ChatGroupMessage } from "../../models/chat-group-message";
import { Participant } from "../../models/participant";
import { User } from "../../models/user";
import { Marketplace } from "../../models/marketplace";
import { MarketplacePost } from "../../models/marketplace-post";

import { log } from "../../helpers/log";

// base
import { BasePage } from "../base";

// config
import { environment } from "../../../environments/environment";
import { dropZoneSettings, fileTypeBlacklist } from "../../settings/dropzone.settings";

declare var cordova: any;

@Component({
    selector: 'app-messages',
    templateUrl: './messages.page.html',
    styleUrls: ['./messages.page.scss'],
})
export class MessagesPage extends BasePage implements OnInit, OnDestroy {

    /**
     * chat box
     *
     * @type ViewChild
     */
    @ViewChild('chatBox') private scrollContainer: ElementRef;

    /**
     * chat box
     *
     * @type ViewChild
     */
    @ViewChild('chatGroupList') private chatGroupList;

    /**
     * dropzone
     *
     * @type ViewChild
     */
    @ViewChild(DropzoneComponent) public dropzone: DropzoneComponent;

    /**
     * unsubscribe subject
     *
     * @type {Subject<void>}
     */
    private ngUnsubscribe: Subject<void> = new Subject<void>();

    /**
     * subscription for event changes subject
     *
     * @type {Subscription}
     */
    private subscriptionEvent: Subscription;

    /**
     * event
     *
     * @type {Event}
     */
    public event: Event;

    /**
     * user
     *
     * @type {User}
     */
    public user: User = new User;

    /**
     * actual opened conversation
     *
     * @type Conversation
     */
    public conversation: Conversation;

    /**
     * broadcasts
     *
     * @type Broadcast[]
     */
    public broadcasts: Broadcast[] = [];

    /**
     * chat group messages
     *
     * @type ChatGroupMessage[]
     */
    public messages: ChatGroupMessage[] = [];

    /**
     * first unread message
     *
     * @type ChatGroupMessage | ConversationMessage
     */
    public firstUnread: ChatGroupMessage | ConversationMessage;

    public unreadCount = 0;

    /**
     * current new message
     *
     * @type {string}
     */
    public messageValue: string = '';

    /**
     * show loading
     *
     * @type {boolean}
     */
    public loading: boolean = true;

    /**
     * show loading in chat box
     *
     * @type {boolean}
     */
    public loadingConversation: boolean = false;

    /**
     * show loading in chat box for infinite loading
     *
     * @type {boolean}
     */
    public loadingInfinite: boolean = false;

    /**
     * fulltext search
     *
     * @type {string}
     */
    public searchMessage: string;

    /**
     * fulltext search
     *
     * @type {string}
     */
    public newMessagesCount: number = 0;

    /**
     * conversation participant
     *
     * @type {Participant}
     */
    public selectedParticipant: Participant;

    /**
     * is selected broadcast
     *
     * @type {booelan}
     */
    public selectedBroadcast: boolean = false;

    /**
     * selected channel chat
     *
     * @type {number}
     */
    public selectedChatGroup: ChatGroup;

    /**
     * submitting new message?
     *
     * @type {booelan}
     */
    public submitting: boolean = false;

    /**
     * user token for appointment download
     */
    public userToken: string;

    /**
     * number of deleted conversations
     *
     * @type number
     */
    public deletedConversationsCount: number;

    /**
     * api
     *
     * @type {string}
     */
    public api: any = environment.api;

    public env: any = environment;

    /**
     * actual page view
     *
     * @type {string}
     */
    public selectedPage: string = 'messages';

    /**
     * is image upload active
     *
     * @type {boolean}
     */
    public sending: boolean = false;

    /**
     * dropzone config
     *
     * @type {DropzoneConfigInterface}
     */
    public dropZoneConfig: DropzoneConfigInterface = dropZoneSettings;

    /**
      * upload state
      *
      * @type {boolean}
      */
    public uploading: boolean = false;

    /**
      * Amount of progress made during an image upload
      *
      * @type number
      */
    public progress: number = 0;

    /**
     * is list of available chat groups visible
     *
     * @type {boolean}
     */
    public addChatGroup: boolean = false;

    /**
     * is detail on right side is visible
     *
     * @type {boolean}
     */
    public detailVisible: boolean = true;

    /**
     * is chat visible
     *
     * @type {boolean}
     */
    public chatVisible: boolean = false;

    /**
     * like loading status
     *
     * @type {boolean}
     */
    public likeLoading: boolean = false;

    /**
     * is limited message window
     *
     * @type {boolean}
     */
    public external: boolean = false;

    // handlers for events
    eventChangeHandler;
    refreshHandler;
    refreshHandlerLive;
    participantSelectedHandler;
    broadcastSelectedHandler;
    chatGroupSelectedHandler;
    chatGroupAddHandler;
    chatGroupAttendeesChangeHandler;
    chatGroupAdminsChangeHandler;
    openCallHandler;
    callAttendeesHandler;
    callAttendeesOpenHandler;

    /**
     * offset for infinite request
     *
     * @type {number}
     */
    private offset: number = 0;

    /**
     * default value for limit infinite request
     *
     * @type {number}
     */
    private limit: number = 50;

    /**
     * check if participant is moderator of chat group
     *
     * @type {boolean}
     */
    public isChatGroupModerator: boolean = false;

    /**
     * Array of statuses for chat group details. Is tab open? (true..)
     *
     * @type {Array<boolean>}
     */
    public chatGroupdetails: Array<boolean> = [
        true,
        false,
        false,
        false,
        false
    ];

    /**
     * Count of chat group attendees
     *
     * @type {number}
     */
    public chatGroupAttendeeCount: number = 0;

    /**
     * List of chat group members
     *
     * @type {Participant[]}
     */
    public chatGroupAttendees: Participant[] = [];

    /**
     * List of video call members
     *
     * @type {Participant[]}
     */
    public videoCallAttendees: Participant[] = [];

    /**
     * is video call enabled
     *
     * @type {boolean}
     */
    public videoCallOpened: boolean = false;

    /**
     * is video call enabled
     *
     * @type {boolean}
     */
    public openVideCallAfterInit: boolean = false;

    // plt before changes, we will use it to reload the state
    pltBackup: any;

    constructor(
        private camera: Camera,
        public ngZone: NgZone,
        private actionSheetController: ActionSheetController,
        private chimeService: ChimeService,
        public modalController: ModalController,
        public broadcastService: BroadcastService,
        public conversationService: ConversationService,
        public chatGroupsService: ChatGroupsService,
        public marketplaceService: MarketplaceService,
        public route: ActivatedRoute,
        public platform: Platform,
        public dropzoneService: DropzoneService
    ) {
        super();

        var self = this;

        // dropzone config
        this.dropZoneConfig = { ...dropzoneService.dropzoneConfig };
        this.dropZoneConfig.maxFilesize = 20;
        this.dropZoneConfig.acceptedFiles = '*';
        this.dropZoneConfig.autoReset = 1000;
        this.dropZoneConfig.autoProcessQueue = false;
        this.dropZoneConfig.init = function () {
            this.on("addedfile", function (file) {
                self.onAddedFile(file);
            });
        }

        this.userLang = this.translate.currentLang;

        this.userToken = localStorage.getItem('token');

        // get query params for linked conversations
        if (this.route.snapshot.queryParams['linkedItem']) {
            this.participantService.linkedItem = JSON.parse(this.route.snapshot.queryParams['linkedItem']);
        }

        if (this.route.snapshot.queryParams['call']) {
            // add some time in case of page refresh
            this.openVideCallAfterInit = true;
        }

        if (this.route.snapshot.queryParams['token']) {
            // add some time in case of page refresh
            this.external = true;
            // hide bottom menu or sidebar as soon as possible
            if (this.plt.tabMenu) {
                this.plt.hideBottomMenu = true;
            } else {
                this.plt.hideSidebar = true;
            }
        }
    }

    /**
     * on init
     *
     * @return void
     */
    ngOnInit() {
        this.eventChangeHandler = () => {
            this.resetDetail({ resetList: !this.plt.tabMenu }, false);
            this.attachEvents();
        }

        // detail is disabled by default on mobile
        if (this.plt.tabMenu) {
            this.detailVisible = false;
        }

        // attach hooks on event change
        this.appEvents.subscribe('event:changed', this.eventChangeHandler);

        // init subscriptions only after user is loaded
        this.userService.getCurrentUser().pipe(
            take(1)
        ).subscribe((user) => {
            this.attachEvents();
        });

        if (this.plt.is('cordova') && cordova.plugins && cordova.plugins.iosrtc) {

            // Expose WebRTC and GetUserMedia SHIM as Globals (Optional)
            // Alternatively WebRTC API will be inside cordova.plugins.iosrtc namespace
            cordova.plugins.iosrtc.registerGlobals();

            // Enable iosrtc debug (Optional)
            // cordova.plugins.iosrtc.debug.enable('*', true);
            // cordova.plugins.iosrtc.debug.disable('*', true);
        }

        this.pltBackup = { ...this.plt };
    }

    /**
     * actions triggered when scrolling in message box up.. load history
     *
     * @return void
     */
    onScrollUp() {
        if (this.selectedChatGroup) {
            this.loadChatGroup(this.selectedChatGroup, true);
        } else if (this.selectedParticipant && this.selectedParticipant.id) {
            this.loadConversation(this.selectedParticipant.id, false, true, null)
        } else {
            if (this.selectedBroadcast) {
                // TODO: add history pagination
                //this.loadBroadcast(null, true);
            }
        }
    }

    attachEvents() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();

        this.refreshHandler = () => {
            this.addChatGroup = false;
            this.resetDetail({ resetList: false }, true);
            this.refresh();
        }

        this.refreshHandlerLive = () => {
            this.addChatGroup = false;
            this.refresh();
        }

        this.openCallHandler = (directOpen = false) => {
            this.openVideCallAfterInit = true;
            if (this.plt.tabMenu) {
                this.plt.hideBottomMenu = true;
            }

            if (directOpen) {
                this.openVideoCall();
            }
        }

        this.participantSelectedHandler = (attendee, conversation = null, menuClick = false) => {
            this.saveDrafts();
            this.searchMessage = '';
            this.selectedPage = 'messages';
            this.addChatGroup = false;
            if (menuClick || (this.participantService.linkedItem && this.participantService.linkedItem.participant_id != attendee.id)) {
                this.participantService.linkedItem = null;
            }
            if (this.plt.tabMenu) {
                this.detailVisible = false;
            }
            if (!conversation) {
                this.selectedParticipant = attendee;
            }
            this.loadConversation(attendee.id, false, false, conversation);
            // check screen size
            this.plt.checkResize(false);
        }

        this.broadcastSelectedHandler = (selectedBroadcast?: Broadcast) => {
            this.saveDrafts();
            this.searchMessage = '';
            this.selectedPage = 'messages';
            this.addChatGroup = false;
            this.participantService.linkedItem = null;
            if (this.plt.tabMenu) {
                this.detailVisible = false;
            }
            this.loadBroadcast(selectedBroadcast);
            // check screen size
            this.plt.checkResize(false);
        }

        this.chatGroupSelectedHandler = (selectedChatGroup?: ChatGroup) => {
            this.saveDrafts();
            this.searchMessage = '';
            this.selectedPage = 'messages';
            this.addChatGroup = false;
            this.participantService.linkedItem = null;
            if (this.plt.tabMenu) {
                this.detailVisible = false;
            }
            this.selectedChatGroup = new ChatGroup(selectedChatGroup);
            this.loadChatGroup(selectedChatGroup);
            // check screen size
            this.plt.checkResize(false);
            this.loading = false;
        }

        this.chatGroupAdminsChangeHandler = (admin) => {
            let oldAdmin = this.selectedChatGroup.admins.find((item) => { return item.id == admin.id });

            if (admin.role) {
                if (oldAdmin) {
                    oldAdmin.role = admin.role;
                } else {
                    this.selectedChatGroup.admins.push(admin);
                }
            } else {
                this.selectedChatGroup.admins = this.selectedChatGroup.admins.filter(item => item.id != admin.id);
            }

            if (this.messages) {
                this.messages.forEach((message) => {
                    if (message.participant) {
                        message.participant.setIsModerator(this.selectedChatGroup);
                        message.participant.setIsOwner(this.selectedChatGroup);
                    }
                });
            }
        }

        this.chatGroupAddHandler = () => {
            this.addChatGroup = true;

            // disable loading in case that on mobile will user click first to this
            this.loading = false;
        }

        this.chatGroupAttendeesChangeHandler = (count) => {
            this.selectedChatGroup.number_attendees = count;
        }

        this.callAttendeesHandler = (attendees) => {
            this.videoCallAttendees = attendees;
        };

        this.callAttendeesOpenHandler = () => {
            this.openVideoCallAttendees();
        };

        this.appEvents.unsubscribe('refresh:messages');
        this.appEvents.unsubscribe('refresh:messages:live');
        this.appEvents.subscribe('refresh:messages', this.refreshHandler);
        this.appEvents.subscribe('refresh:messages:live', this.refreshHandlerLive);

        this.appEvents.unsubscribe('messages:participant');
        this.appEvents.unsubscribe('messages:broadcast');
        this.appEvents.unsubscribe('messages:chatgroup');
        this.appEvents.unsubscribe('messages:call');
        this.appEvents.unsubscribe('call:attendees');
        this.appEvents.unsubscribe('call:attendees:open');
        this.appEvents.subscribe('messages:participant', this.participantSelectedHandler);
        this.appEvents.subscribe('messages:broadcast', this.broadcastSelectedHandler);
        this.appEvents.subscribe('messages:chatgroup', this.chatGroupSelectedHandler);
        this.appEvents.subscribe('messages:call', this.openCallHandler);
        this.appEvents.subscribe('call:attendees', this.callAttendeesHandler);
        this.appEvents.subscribe('call:attendees:open', this.callAttendeesOpenHandler);

        this.appEvents.unsubscribe('chatgroup:admins:changed');
        this.appEvents.subscribe('chatgroup:admins:changed', this.chatGroupAdminsChangeHandler);

        this.appEvents.unsubscribe('messages:chatgroup:add');
        this.appEvents.subscribe('messages:chatgroup:add', this.chatGroupAddHandler);

        this.appEvents.unsubscribe('chatGroup:attendees:changed');
        this.appEvents.subscribe('chatGroup:attendees:changed', this.chatGroupAttendeesChangeHandler);

        this.appEvents.unsubscribe('messages:empty');
        this.appEvents.subscribe('messages:empty', (setting: { reset: boolean } = { reset: false }) => {
            if (setting.reset) {
                this.resetDetail({ resetList: false });
            }
            this.loading = false;
        });

        this.ngUnsubscribe = new Subject<void>();

        this.userService.getCurrentUser().pipe(
            takeUntil(this.ngUnsubscribe),
            debounceTime(0)
        ).subscribe((user) => {
            if (user.id) {

                this.user = user;

                // cancel previous subscription
                if (this.subscriptionEvent) {
                    this.subscriptionEvent.unsubscribe();
                }

                this.subscriptionEvent = this.eventService.getCurrentEvent().pipe(
                    takeUntil(this.ngUnsubscribe),
                    debounceTime(0)
                ).subscribe((event) => {

                    // event was changed, reset component setup
                    if (!this.event || this.event.id != event.id) {
                        this.resetDetail({ resetList: false }, false);
                    }

                    this.event = event;
                    // NOTE[jg] not needed, done via chat-group-list component
                    // this.refresh();
                });
            }
        });

        this.router.events.pipe(
            takeUntil(this.ngUnsubscribe)
        ).subscribe((event: any) => {
            // for route event event get route event
            if ((event instanceof Scroll)) {
                event = <RouterEvent>event.routerEvent;
            }

            // using url from NavigationEnd instance, because route.snapshot was not sometimes updated...
            if (event instanceof RouterEvent && event instanceof NavigationEnd) {
                if (event.url.indexOf('?call=true') !== -1) {
                    this.openVideCallAfterInit = true;
                }
            }
        });
    }

    ionViewDidLeave() {
        // start loading for next visit again
        this.loading = true;
    }

    /**
      * refresh list
      *
      * @return void
      */
    public refresh($event?, publish: boolean = false) {
        // publish event for message list component if user pull
        if (publish) {
            this.appEvents.publish('messages:refresh:list');
        }
        if (this.selectedChatGroup) {
            this.loadChatGroup(this.selectedChatGroup, false, $event);
        } else if (this.selectedParticipant && this.selectedParticipant.id) {
            this.loadConversation(this.selectedParticipant.id, false, false, null, $event)
        } else {
            if (this.selectedBroadcast) {
                this.loadBroadcast(null, false, $event);
            } else {
                if ($event) {
                    $event.target.complete();
                }
            }
        }
    }

    /**
     * load conversation with selected participant
     *
     * @param {number} attendeeId
     *
     * @return void
     */
    public loadConversation(attendeeId: number, newConversation: boolean = false, infiniteScroll: boolean = false, conversation = null, $event?): void {
        if (this.plt.eventInChange) {
            return;
        }

        if (!infiniteScroll) {
            this.offset = 0;
            this.loadingConversation = true;
            this.loadingInfinite = false;

            // check message drafts and restore message if some is stored
            if (this.conversationService.directMessageDrafts[attendeeId]) {
                this.messageValue = this.conversationService.directMessageDrafts[attendeeId];
            }
        } else {
            this.loadingInfinite = true;
        }

        this.selectedBroadcast = false;
        this.selectedChatGroup = null;

        // to avoid empty loading
        if (conversation) {
            this.selectedParticipant = new Participant(conversation.participants.filter(
                (participant) => {
                    return participant.id !== this.user.selected_participant_id;
                }
            )[0]);
        }

        // check valid link
        if (this.selectedParticipant && this.selectedParticipant.id != attendeeId) {
            // if link is not proper, change route to proper one
            if (this.plt.isUrl('tabs/messages')) {
                this.router.navigate([this.plt.defaultLink + '/messages/direct/' + attendeeId], { skipLocationChange: false });
            }
            this.selectedParticipant.id = attendeeId;
        }

        if (this.plt.tabMenu) {
            this.plt.hideBottomMenu = true;
        }

        let subscription = this.conversationService.getByParticipantId(
            + attendeeId,
            this.offset,
            this.limit,
            this.searchMessage
        ).subscribe(
            (success) => {
                // check if user is still looking at the same participants messages
                // data loading is asynchronous and user can still change chat group or conversation
                if (this.selectedParticipant && this.selectedParticipant.id == attendeeId) {

                    // move offset to next level those are not last items
                    if (success.conversation.messages.length > 0) {
                        this.offset = this.offset + this.limit;
                    }

                    if (!infiniteScroll) {
                        // create and set up conversation
                        this.conversation = new Conversation(success.conversation);

                        // set proper order of messages and values for connected attributes
                        this.prepareConversation();

                    } else {
                        // push new messages into existing conversation
                        success.conversation.messages.forEach((message, i) => {
                            if (message.system_message == 'LINKED_ITEM') {
                                // add this additional info to last one
                                let linkedItem = JSON.parse(message.message);
                                linkedItem.clickable = false
                                // check if link should be clickable
                                this.conversation.messages[this.conversation.messages.length - 1].linkedItem = linkedItem;
                                this.verifyLinkedItem(this.conversation.messages[this.conversation.messages.length - 1].linkedItem);
                            } else {
                                this.conversation.messages.unshift(new ConversationMessage(message));
                            }
                        });

                        // unmark proper count of unread messages in main menu
                        let readCount = this.conversation.messages.filter((message) => !!message.unread && this.selectedParticipant.id === message.participant_id).length;
                        this.plt.readMessageNotification(readCount);
                    }

                    // find first unsread if there is any
                    this.firstUnread = this.conversation.messages.find((message) => !!message.unread && this.selectedParticipant.id === message.participant_id)
                    if (this.firstUnread) {
                        this.firstUnread.firstNew = true;
                        this.unreadCount = this.conversation.messages.filter((message) => !!message.unread && this.selectedParticipant.id === message.participant_id).length;
                    }

                    setTimeout(() => {
                        if ($event) {
                            $event.target.complete();
                        } else {
                            this.loading = false;
                        }

                        // scroll to last message if user is not scrolling up in history
                        if (!infiniteScroll) {
                            this.scrollChatToBottom();
                        } else {
                            if (success.conversation.messages.length > 0 || this.searchMessage) {
                                this.loadingInfinite = false;
                            }
                        }
                        if (this.openVideCallAfterInit) {
                            setTimeout(() => {
                                this.openVideoCall();
                            }, 200);
                        }
                    }, 200);

                    // mark broadcast messages as read
                    let subscriptionMarkAsRead = this.conversationService.markAsRead(
                        this.selectedParticipant.id
                    ).subscribe(
                        () => {
                            log('info', 'loadConversation', this.conversation);
                        },
                        (error) => {
                            log('error', 'loadConversation', error);
                        }
                    );
                    this.plt.activeSubscriptions.push(subscriptionMarkAsRead);
                }
            },
            (error) => {

                log('error', 'loadConversation', error);
                if ($event) {
                    $event.target.complete();
                } else {
                    this.loading = false;
                }

                if (this.plt.tabMenu) {
                    this.plt.hideBottomMenu = false;
                }

                // hide chat box loading loading
                this.loadingConversation = false;
            }
        );
        this.plt.activeSubscriptions.push(subscription);
    }

    /**
     * prepare data for actual conversation
     *
     * @return void
     */
    private prepareConversation() {
        this.selectedParticipant = new Participant(this.conversation.participants.filter(
            (participant) => {
                return participant.id !== this.user.selected_participant_id;
            }
        )[0]);

        // TOOD[jg] why??
        // if (!!this.user.selected_participant.likes) {
        //     this.user.selected_participant.likes.forEach(like => {
        //         if (like.id === this.selectedParticipant.id) {
        //             this.selectedParticipant.liked = true;
        //         }
        //     });
        // }
        //
        // if (!!this.user.selected_participant.bookmarks) {
        //     this.user.selected_participant.bookmarks.forEach(bookmark => {
        //         if (bookmark.id === this.selectedParticipant.id) {
        //             this.selectedParticipant.bookmarked = true;
        //         }
        //     });
        // }

        // reverse array, because newest message will be latest
        // endpoint is ordering in DESC mode to allow history loading
        this.conversation.messages.reverse();

        this.conversation.messages.forEach((message, i) => {
            if (message.linkedItem) {
                this.verifyLinkedItem(message.linkedItem);
            }

            if (message.appointment) {
                message.appointment = new Appointment(message.appointment);
                message.appointment.setParticipantStatus(this.user.selected_participant_id);
            }
        });

        // unmark proper count of unread messages in main menu
        let readCount = this.conversation.messages.filter((message) => !!message.unread && this.selectedParticipant.id === message.participant_id).length;
        this.plt.readMessageNotification(readCount);
    }

    /**
     * load broadcast for selected attendee
     *
     * @param {number} attendeeId
     *
     * @return void
     */
    public loadBroadcast(selectedBroadcast?: Broadcast, infiniteScroll: boolean = false, $event?): void {

        // NOTE[jg] broadcast is not using infinity history loading.. allways load all messages now..

        if (!infiniteScroll) {
            this.offset = 0;
            this.loadingConversation = true;
            this.loadingInfinite = false;
        } else {
            this.loadingInfinite = true;
        }

        this.loadingConversation = true;

        this.conversation = null;
        this.selectedParticipant = null;

        this.selectedChatGroup = null;
        this.selectedBroadcast = true;
        this.broadcasts = [];

        let subscription = this.broadcastService.getAllByParticipantId(
            this.user.selected_participant_id,
            this.searchMessage
        ).subscribe(
            (broadcasts: Broadcast[]) => {

                if (selectedBroadcast) {
                    selectedBroadcast.pivot.unread = 0;
                }

                let firstNew = false;
                let readCount = 0;
                broadcasts.forEach(broadcast => {
                    if (broadcast.pivot.unread && !firstNew) {
                        broadcast.firstNew = true;
                        firstNew = true;
                    }
                    if (broadcast.pivot.unread) {
                        readCount++;
                    }
                    this.broadcasts.push(new Broadcast(broadcast));
                });

                // mark broadcast messages as read
                let subscriptionMarkAsRead = this.broadcastService.markAsRead(
                    broadcasts.map(object => object.id),
                    this.user.selected_participant_id
                ).subscribe(
                    (broadcasts: Broadcast[]) => {
                        log('info', 'loadBroadcast', broadcasts);
                        this.plt.readMessageNotification(readCount);
                    },
                    (error) => {
                        log('error', 'loadBroadcast', error);
                    }
                );
                this.plt.activeSubscriptions.push(subscriptionMarkAsRead);

                if ($event) {
                    $event.target.complete();
                } else {
                    this.loading = false;
                }

                this.scrollChatToBottom();
            },
            (error) => {

                log('error', 'loadBroadcast', error);
                if ($event) {
                    $event.target.complete();
                } else {
                    this.loading = false;
                }

                // hide chat box loading loading
                this.loadingConversation = false;
            });
        this.plt.activeSubscriptions.push(subscription);
    }

    /**
     * load chatgroup
     *
     * @param {ChatGroup} chatGroup
     *
     * @return void
     */
    public loadChatGroup(chatGroup?: ChatGroup, infiniteScroll: boolean = false, $event?): void {
        // check if event is not in the middle of change
        if (this.plt.eventInChange) {
            return;
        }

        if (this.selectedChatGroup?.stream) {
            this.detailVisible = false;
            this.plt.hideSidebar = true;
        }

        if (!infiniteScroll) {
            this.offset = 0;
            this.loadingConversation = true;
            this.loadingInfinite = false;

            // check message drafts and restore message if some is stored
            if (this.conversationService.groupMessageDrafts[chatGroup.id]) {
                this.messageValue = this.conversationService.groupMessageDrafts[chatGroup.id];
            }
        } else {
            this.loadingInfinite = true;
        }

        this.selectedParticipant = null;
        this.selectedBroadcast = false;
        this.conversation = null;
        this.broadcasts = [];

        if (this.plt.tabMenu) {
            this.plt.hideBottomMenu = true;
        }

        // we want to load messages if it is normal chat group or stream with chat displayed
        if (!this.selectedChatGroup?.stream || this.selectedChatGroup?.stream?.display_chat) {
            let subscription = this.chatGroupsService.getMessagesByChannelId(
                this.selectedChatGroup.id,
                this.offset,
                this.limit,
                this.searchMessage
            ).subscribe(
                (success) => {
                    // check if user is still in same chat group when data are loaded
                    // data loading is asynchronous and user can still change chat group or conversation
                    if (this.selectedChatGroup && this.selectedChatGroup.id == chatGroup.id) {

                        // move offset to next level those are not last items
                        if (success.messages.length > 0) {
                            this.offset = this.offset + this.limit;
                        }

                        // for default call reset list of messages
                        if (!infiniteScroll) {
                            this.messages = [];
                        }

                        this.prepareChatGroup(success);

                        if ($event) {
                            $event.target.complete();
                        } else {
                            this.loading = false;
                        }

                        if (this.openVideCallAfterInit) {
                            setTimeout(() => {
                                this.openVideoCall();
                            }, 200);
                        }

                        // scroll to last message if user is not scrolling up in history
                        if (!infiniteScroll) {
                            this.scrollChatToBottom();
                        } else {
                            if (success.messages.length > 0 || this.searchMessage) {
                                this.loadingInfinite = false;
                            }
                        }
                    }
                },
                (error) => {
                    const data = error.error;
                    this.overlayService.showError(data.message);

                    if (data.leave_group) {
                        this.checkDeletedFromGroup(data);
                    } else {
                        this.router.navigate([this.plt.defaultLink.replace('/', '') + 'messages']);
                        this.appEvents.publish('messages:refresh:list');
                    }

                    if ($event) {
                        $event.target.complete();
                    } else {
                        this.loading = false;
                    }

                    if (this.plt.tabMenu) {
                        this.plt.hideBottomMenu = false;
                    }

                    // hide chat box loading loading
                    this.loadingConversation = false;
                });
            this.plt.activeSubscriptions.push(subscription);
        }
        // if it is stream without chat displayed
        else if (this.selectedChatGroup?.stream) {
            this.loading = false;
            this.loadingConversation = false;
        }
    }

    private prepareChatGroup(success, addOnlyNew = false) {

        let lastId = 0;
        if (this.messages.length) {
            lastId = this.messages[this.messages.length - 1].id;
        }

        let checkMessage = null;

        success.messages.forEach(
            (message, index) => {
                let messageObjNext = null

                let messageObj = new ChatGroupMessage(message);
                if (success.messages[index + 1]) {
                    messageObjNext = new ChatGroupMessage(success.messages[index + 1]);
                }

                if (messageObj.participant && this.selectedChatGroup) {
                    messageObj.participant.setIsModerator(this.selectedChatGroup);
                    messageObj.participant.setIsOwner(this.selectedChatGroup);
                }

                if (messageObj.message == 'CHAT_GROUP_CALL_STARTED') {
                    messageObj.message = '';
                    messageObj.linkedItem = {
                        id: this.selectedChatGroup.id,
                        text: '',
                        type: 'group-call',
                        clickable: false
                    };
                    // check if there are any attendees to show join button, only for last one in chat group
                    if (addOnlyNew || !checkMessage) {
                        checkMessage = messageObj;
                    }
                }

                // check if message is unread and mark as new only first one
                // also ignore my own messages for this condition...
                if (this.selectedChatGroup && (((messageObj.date_created_at > this.selectedChatGroup.date_read_at))
                    && ((messageObjNext == null) || (messageObjNext.date_created_at < this.selectedChatGroup.date_read_at))
                    && messageObj.participant_id != this.user.selected_participant_id
                )) {
                    messageObj.firstNew = true;

                    this.firstUnread = messageObj;
                    this.unreadCount += 1;
                }

                // add only message with highter ID then last message in chat
                if (addOnlyNew) {
                    if (lastId < messageObj.id) {
                        this.messages.push(messageObj);
                        // move offset to count with those new message
                        this.offset++;
                    }
                } else {
                    this.messages.unshift(messageObj);
                }
            }
        );

        // check if call is allowed, or it is appointment with call
        if (checkMessage && this.selectedChatGroup.callIsAllowed() && !this.user.selected_participant.invisible) {
            // verify message if button to join should be visible
            this.chimeService.checkByChatGroupId(this.selectedChatGroup.id).subscribe(
                (success) => {
                    if (success.attendees_available) {
                        checkMessage.linkedItem.clickable = true;
                        // checkMessage.message = this.translate.instant('CHAT_GROUP_CALL_STARTED_JOIN');
                    }
                },
                (error) => {
                    const data = error.error;
                    // if video calls were disabled
                    if (data.call_disabled) {
                        checkMessage.linkedItem.clickable = false;
                        // disable calls button
                        this.selectedChatGroup.allow_call = false;
                        // close video call if opened and inform user
                        if (this.videoCallOpened) {
                            this.closeVideoCall();
                            // show error message
                            this.showError(data.message);
                        }
                    }
                }
            )
        }

        this.user.selected_participant.setIsOwner(this.selectedChatGroup);

        // mark chat group messages as read
        if (this.selectedChatGroup) {
            let subscription = this.chatGroupsService.markAsRead(
                this.selectedChatGroup.id
            ).subscribe(
                (success) => {
                    log('info', 'markChatGroupAsRead', success);
                    if (this.selectedChatGroup && this.selectedChatGroup.unread_messages_count > 0) {
                        this.plt.readChatGroupMessageNotification(this.selectedChatGroup.unread_messages_count);
                    }
                },
                (error) => {
                    log('error', 'loadBroadcast', error);
                }
            );
            this.plt.activeSubscriptions.push(subscription);
        }
    }

    public async openDestroyMessage(message: ConversationMessage | ChatGroupMessage) {
        this.overlayService.showConfirm(
            this.translate.instant('MESSAGE_DELETE_MODAL_TITLE'),
            this.translate.instant('MESSAGE_DELETE_MODAL_HINT'),
            () => { this.deleteMessage(message) },
            () => { },
            this.translate.instant('MESSAGE_DELETE_MODAL_BUTTON')
        );
    }

    /**
     * delete message
     *
     * @return void
     */
    public deleteMessage(message: ConversationMessage | ChatGroupMessage) {
        if (message instanceof ConversationMessage) {
            let subscription = this.conversationService.deleteMessage(message.id)
                .subscribe((response) => {
                    message.deleted_at = response.deleted_at;
                });
            this.plt.activeSubscriptions.push(subscription);
        } else if (message instanceof ChatGroupMessage) {
            // Chat group message
            let subscriptionDelete = this.chatGroupsService.deleteMessage(message.id, message)
                .subscribe(
                    (response) => {
                        message.deleted_at = response.deleted_at;
                    },
                    (error) => {
                        const data = error.error;

                        this.showError(data.message);
                        this.checkDeletedFromGroup(data);
                    }
                );
            this.plt.activeSubscriptions.push(subscriptionDelete);
        }
    }

    public unsetFirstNew() {
        if (this.selectedBroadcast) {
            this.broadcasts.map(item => item.firstNew = false);
        }
        else if (this.selectedChatGroup) {
            this.messages.map(item => item.firstNew = false);
        }
        else if (this.selectedParticipant) {
            this.conversation.messages.map(item => item.firstNew = false);
        }
    }

    /**
     * send chat message
     *
     * @return void
     */
    public sendMessage(): void {

        // Prevent empty messages
        if (!this.messageValue || this.submitting) {
            return;
        } else {
            this.submitting = true;
            //this.loadingConversation = true;

            if (this.selectedChatGroup) {

                let newMessage = new ChatGroupMessage;
                newMessage.message = this.messageValue;

                let subscription = this.chatGroupsService.sendMessage(this.selectedChatGroup.id, newMessage)
                    .subscribe(
                        (success) => {

                            // reset offset, we load only latest messages after sending new one
                            // se prepare offset to load next page...
                            // this.offset = this.limit;
                            // allow to load history
                            // this.loadingInfinite = false;
                            // empty messags, we load only tatest
                            //this.messages = [];

                            this.prepareChatGroup(success, true);

                            // empty send input
                            this.messageValue = '';

                            // unmark last first unread message to avoid auto scroll to this new one
                            if (this.firstUnread) {
                                this.firstUnread.firstNew = null;
                                this.firstUnread = null;
                                this.unreadCount = 0;
                            }

                            this.saveDrafts();

                            this.unsetFirstNew();

                            this.scrollChatToBottom();
                            this.submitting = false;
                        }, error => {

                            const data = error.error;

                            this.showError(data.message);
                            this.submitting = false;
                            this.loadingConversation = true;

                            this.checkDeletedFromGroup(data);
                        });
                this.plt.activeSubscriptions.push(subscription);
            } else {

                let conversationId = this.conversation.id;
                let newMessage = new ConversationMessage;
                newMessage.message = this.messageValue;
                newMessage.linkedItem = this.participantService.linkedItem;

                let subscriptionConversation = this.conversationService.sendMessage(conversationId, newMessage)
                    .subscribe(
                        (conversation: Conversation) => {
                            this.participantService.linkedItem = null;
                            // reset offset, we load only latest messages after sending new one
                            // se prepare offset to load next page...
                            // this.offset = this.limit;
                            // allow to load history
                            // this.loadingInfinite = false;

                            let lastId = 0;
                            if (this.conversation.messages.length) {
                                // if there is linked intem, then last message will be this linked headline
                                lastId = this.conversation.messages[this.conversation.messages.length - 1].id;
                            }

                            // push new messages into existing conversation
                            conversation.messages.forEach((message, i) => {

                                if (lastId < message.id) {
                                    // don't show linked messages
                                    if (message.system_message == 'LINKED_ITEM') {
                                        if (conversation.messages[i + 1]) {
                                            conversation.messages[i + 1].linkedItem = JSON.parse(message.message);
                                            conversation.messages[i + 1].linkedItem.clickable = true;
                                        }
                                    } else {
                                        this.conversation.messages.push(new ConversationMessage(message))
                                        // move offset to count with those new message
                                        this.offset++;
                                    }
                                }
                            });

                            // empty send input
                            this.messageValue = '';

                            this.unsetFirstNew();

                            this.scrollChatToBottom();
                            this.submitting = false;
                        }, error => {

                            const data = error.error;

                            this.showError(data.message);
                            if (data.error == 'Unauthorized') {
                                this.selectedParticipant.invisible = true;
                            }

                            this.submitting = false;
                        });
                this.plt.activeSubscriptions.push(subscriptionConversation);
            }
        }
    }

    private checkDeletedFromGroup(data) {
        if (data.leave_group) {
            if (this.plt.tabMenu) {
                this.resetDetail();
            } else {
                // close video call if opened
                if (this.videoCallOpened) {
                    this.closeVideoCall();
                }
            }

            this.appEvents.publish('messages:refresh:list');
        }
    }

    /**
     * close modal window after message was sended
     *
     */
    public messageSended(conversation?: Conversation) {
        if (!this.selectedChatGroup) {
            this.conversation = new Conversation(conversation);
            // set proper order of messages and values for connected attributes
            this.prepareConversation();
        }

        // show last message -> chat needs to be scrolled to bottom
        this.scrollChatToBottom();
        this.submitting = false;
    }

    /**
     * scroll chat to bottom
     *
     * @return void
     */
    private scrollChatToBottom(): void {
        // only works with timeout and not in lifecycle hook
        // @todo: refactor
        try {
            setTimeout(() => {
                // with timeout because the scrollHeight get changed
                // not in lifecycle hook because of jumping to bottom if scrolling up
                if (this.scrollContainer) {

                    let el: HTMLElement
                    let messageCount: number = 0
                    if (this.conversation) {
                        messageCount = this.conversation.messages.length;
                    } else if (this.broadcasts.length > 0) {
                        messageCount = this.broadcasts.length;
                    } else if (this.messages.length > 0) {
                        messageCount = this.messages.length;
                    }

                    // if there are some messages
                    if (messageCount > 0) {
                        // let scrollOffset = this.scrollContainer.nativeElement.scrollHeight;
                        // try to find header with new messsages
                        el = document.querySelector('.new-message-header');
                        if (!el) {
                            // if there is no header, find start of latest message, +1 because of overlay span
                            // el = document.querySelector('.chat-box-list > div:nth-child(' + (messageCount) + ')');
                            el = document.querySelector('.chat-box-list > .end');
                            // scrollOffset = this.scrollContainer.nativeElement.scrollHeight - el.clientHeight - 65;
                        } else {
                            //scrollOffset = el.parentElement.offsetHeight;
                        }

                        // scroll to last message header if founded
                        if (el) {
                            // we will not use scrollIntoView on mobile devices, because it si moving whole layout...
                            if (!this.plt.sizeSm) {
                                // replaced calculated scroll with brorwsers function which scroll el to be visible
                                el.scrollIntoView();
                                this.scrollContainer.nativeElement.scrollTop = this.scrollContainer.nativeElement.scrollTop;
                            } else {
                                // add 65px scroll to see header
                                this.scrollContainer.nativeElement.scrollTop = el.offsetTop + 64;
                            }
                            setTimeout(() => {
                                this.loadingConversation = false;
                            }, 150);
                        } else {
                            // otherwise try it again after some time
                            this.scrollChatToBottom();
                        }
                    } else {
                        // hide loading if there is any conversation yet
                        this.loadingConversation = false;
                    }
                } else {
                    this.loadingConversation = false;
                }
            }, 300);
        } catch (err) {
            this.loadingConversation = false;
            return;
        }
    }

    /**
     * scroll chat to bottom
     *
     * @return void
     */
    public scrollPageToBottom(): void {
        try {
            setTimeout(() => {
                // scroll whole page to bottom, used for keyboard to focus textarea
                window.scrollTo(0, document.body.scrollHeight);
            }, 150);
        } catch (err) {
            return;
        }
    }

    /**
     * collection sorting by date
     *
     * @param collection
     *
     * @return {Conversation[]}
     */
    private sortCollection(collection: any[]): any[] {
        return collection.sort((n1, n2) => {
            let n1value: Date = new Date;
            let n2value: Date = new Date;

            if (n1 instanceof Broadcast) {
                n1value = n1.date_sent_at;
            } else if (n1 instanceof Conversation) {
                n1value = n1.latest_message.date_created_at;
            }

            if (n2 instanceof Broadcast) {
                n2value = n2.date_sent_at;
            } else if (n2 instanceof Conversation) {
                n2value = n2.latest_message.date_created_at;
            }

            return n1value < n2value ? 1 : n1value == n2value ? 0 : -1;
        });
    }

    /**
     * answer appointment
     *
     * @param appointment
     * @param status
     *
     * @return void
     */
    public answerAppointment(appointment: Appointment, status: number) {
        this.loadingConversation = true;
        const oldStatus = appointment.status;
        appointment.status = status;
        appointment.participantStatus = status;

        let subscription = this.conversationService.answerAppointment(this.conversation.id, appointment)
            .subscribe((conversation: Conversation) => {

                // mark slot as blocked
                // TODO
                // if (this.user.selected_participant.event.use_timeslots && appointment.status == 1) {
                //     this.participantTimeslot.markAppointment([appointment]);
                // }

                this.conversation = new Conversation(conversation);
                appointment.status = oldStatus;

                // set proper order of messages and values for connected attributes
                this.prepareConversation();

                this.loadingConversation = false;

                this.scrollChatToBottom();

            }, error => {

                this.showError(error.error.message);
                appointment.status = oldStatus;
                appointment.participantStatus = oldStatus;
                this.loadingConversation = false;
            });
        this.plt.activeSubscriptions.push(subscription);
    }

    /**
     * go to participants detail page
     *
     * @param participant
     *
     * @return void
     */
    public async showAttendeeDetail(attendee: Participant, appointmentRequest = true) {
        if (!this.user.canProfileDetails(attendee)) {
            return false;
        }

        this.routerExtService.softNavigate();

        const modal = await this.modalController.create({
            component: AttendeeDetailComponent,
            cssClass: 'attendee-modal',
            componentProps: {
                attendee: attendee,
                appointmentRequest: appointmentRequest,
                // if we are using direct chat with this participant...
                // app will handle closing modal different way
                source: this.selectedParticipant ? 'messages' : ''
            }
        });

        modal.onWillDismiss().then((data) => {
            if (data.data && this.selectedParticipant) {
                this.loadConversation(this.selectedParticipant.id);
            }
            if (data.data && data.data.action && data.data.action == 'close') {
                this.routerExtService.softBack();
            }
        });

        return await modal.present();
    }

    resetDetail(setting: { resetList: boolean } = { resetList: true }, goToMessages: boolean = true) {
        // safe drafts
        this.saveDrafts();
        // reset all attributes
        this.addChatGroup = false;
        this.searchMessage = '';
        if (!this.external) {
            this.plt.hideBottomMenu = false;
        }
        this.selectedBroadcast = false;
        this.selectedChatGroup = null;
        this.selectedParticipant = null;
        // close video call if opened
        if (this.videoCallOpened) {
            this.closeVideoCall();
        }
        if (this.plt.tabMenu && this.chatGroupList) {
            this.chatGroupList.resetDetail(goToMessages);
        }
        // public this reset change to other components
        if (setting.resetList) {
            this.appEvents.publish('messages:refresh:list', null, true);
        }
    }

    /**
     * download appointment
     *
     * @return void
     */
    public download(appointment: Appointment) {
        if (this.plt.is('cordova')) {
            appointment.participants = [this.selectedParticipant];
            this.plt.addToCalendar(appointment, this.user.selected_participant_id);
        } else {
            window.location.href = this.api + '/appointment/download/' + appointment.id + '?token=' + this.userToken;
        }
    }

    /**
     * select page
     *
     * @param {string} page
     */
    public selectPage(page: string) {
        this.selectedPage = page;
        if (this.selectedPage == 'notifications' && localStorage.getItem('token')) {
            setTimeout(() => {
                this.participantService.setFeedsVisited().subscribe();
                this.plt.unreadNotifications = 0;
            }, 2000);
        }
    }

    /**
     * trigger actions for message box focus
     *
     */
    public messageBoxFocus() {
        // scroll to last message
        this.scrollPageToBottom();
        // check page resizing after keyboard is opened
        this.plt.checkResize();
    }

    /**
     * edit message
     *
     * @param ConversationMessage
     *
     * @return void
     */
    public editMessage(message: ConversationMessage) {
        if (this.plt.sizeSm) {
            this.editMessageModal(message)
        } else {
            // use output to emit edit
            message.edit = true
        }
    }

    /**
     * open modal with message add form
     * @return void
     */
    public async editMessageModal(message: ConversationMessage) {

        this.routerExtService.softNavigate();

        const modal = await this.modalController.create({
            component: EditMessageComponent,
            cssClass: 'message-form',
            componentProps: {
                modal: true,
                displayBigTextarea: true,
                message: message,
                conversationId: this.conversation ? this.conversation.id : null
            }
        });

        modal.onWillDismiss().then((data) => {
            if (data.data) {
                // use output to refresh list
                //this.refresh()
                if (data.data.action && data.data.action == 'close') {
                    this.routerExtService.softBack();
                }

                if (data.data.message) {
                    message = data.data.message;
                }
            }
        });

        return await modal.present();
    }

    /**
     * open modal with message add form
     * @return void
     */
    public async openVideo() {

        this.routerExtService.softNavigate();

        const modal = await this.modalController.create({
            component: ChimeComponent,
            cssClass: 'message-form',
            componentProps: {
                modal: true,
                participant: this.selectedParticipant
            }
        });

        modal.onWillDismiss().then((data) => {
            if (data.data) {
                // use output to refresh list
                //this.refresh()
                if (data.data.action && data.data.action == 'close') {
                    this.routerExtService.softBack();
                }
            }
        });

        return await modal.present();
    }

    /**
     * close modal window
     *
     */
    public messageEditationCanceled(event?: ConversationMessage | ChatGroupMessage) {
        // copy the original object back into message
        if (this.conversation) {
            this.conversation.messages.map(el => {
                if (el.id == event.id) {
                    return Object.assign(el, event)
                }
                return el;
            });
        }
        if (this.selectedChatGroup) {
            this.messages.map(el => {
                if (el.id == event.id) {
                    return Object.assign(el, event)
                }
                return el;
            });
        }
    }

    /**
     * on image upload
     *
     * @param args
     */
    public onAddedFile(file) {
        this.sending = true;

        const reader = new FileReader();

        reader.onload = () => {
            var dataURL = reader.result;

            // create message object for chat group
            var message = this.selectedChatGroup ? new ChatGroupMessage() : new ConversationMessage();
            message.file = dataURL;
            message.message = this.messageValue;
            message.linkedItem = this.participantService.linkedItem;

            // check the filesize before open modal
            if (file.size > (this.dropZoneConfig.maxFilesize * 1000000)) {
                this.overlayService.showError(this.translate.instant('DROPZONE_FILE_TOO_BIG', {
                    'filesize': Math.ceil(file.size / 1000000),
                    'maxFilesize': this.dropZoneConfig.maxFilesize
                }));
            } else {
                this.showMessageFileModal(message, file.name);
            }

        };

        if (file) {
            // check unsupported file type
            if (fileTypeBlacklist.indexOf(file.type) === -1) {
                reader.readAsDataURL(file);
            } else {
                this.overlayService.showError(this.translate.instant('MESSAGES_UPLOAD_UNSUPPORTED_FILE_TYPE'));
                if (!this.plt.is('cordova')) {
                    this.dropzone.directiveRef.reset();
                }
            }
        }
    }

    public async openMobileFileSelection() {
        // show action sheet with file/camera selection on mobile

        let buttons = [{
            text: this.translate.instant('MESSAGES_OPEN_CAMERA'),
            handler: () => {
                // open camera
                this.openCamera();
            }
        }, {
            text: this.translate.instant('MESSAGES_OPEN_FILES'),
            handler: () => {
                // open file selection via dropzone
                this.dropzone.directiveRef.dropzone().clickableElements[0].click()
            }
        }, {
            text: this.translate.instant('BUTTON_CANCEL'),
            role: 'cancel',
            handler: () => {

            }
        }];

        const actionSheet = await this.actionSheetController.create({
            header: '',
            buttons: buttons,
        });
        await actionSheet.present();
    }

    /**
     * Opens the camera and display the taken picture if available
     */
    public async openCamera(detail = null): Promise<void> {
        this.plt.reloadable = false;

        try {
            let image = await this.cameraManager();

            if (image) {

                let name = Helper.guid() + '.jpg';
                // add base64 string to message
                // create message object for chat group
                var message = this.selectedChatGroup ? new ChatGroupMessage() : new ConversationMessage();
                message.file = 'data:image/png;base64, ' + image;
                message.message = this.messageValue;
                message.linkedItem = this.participantService.linkedItem;

                this.showMessageFileModal(message, name);
            } else {
                this.plt.reloadable = true;
            }
        } catch (exception) {
            this.plt.reloadable = true;
        }
    }

    /**
     * Opens the camera so that the user can take a picture
     *
     * @return The url of the taken image
     */
    private cameraManager(): Promise<string> {
        return this.camera.getPicture({
            // We want to have the URL to the file
            destinationType: this.camera.DestinationType ? this.camera.DestinationType.DATA_URL : 0,
            // Source of the images is the camera
            sourceType: this.camera.PictureSourceType ? this.camera.PictureSourceType.CAMERA : 1,
            // Encoding type is JPEG
            encodingType: this.camera.EncodingType ? this.camera.EncodingType.JPEG : 0,
            // Give us the full quality of the image, lower it for better performance
            quality: 90,
            // Allow editing of the image after its taken
            allowEdit: false,
            // When a image is taken via the camera also save it to the native photo album
            saveToPhotoAlbum: false,
            // Correct the orrientation of the image
            correctOrientation: true,
            // Resize to save bandwidth and prevent rejection by the server
            targetWidth: 2880,
            // targetHeight: 1800
        });
    }

    /**
     * show message file form modal
     *
     * @param message
     *
     * @return void
     */
    public async showMessageFileModal(message: ChatGroupMessage | ConversationMessage, filename?: string) {
        if (!this.plt.is('cordova')) {
            this.dropzone.directiveRef.reset();
        }

        this.routerExtService.softNavigate();

        const modal = await this.modalController.create({
            component: MessageFileFormComponent,
            cssClass: 'message-file-form-modal',
            componentProps: {
                message: message,
                parentObject: this.selectedChatGroup || this.conversation,
                filename: filename
            }
        });

        modal.onWillDismiss().then((data) => {
            if (data.data && data.data.action && data.data.action == 'close') {
                this.routerExtService.softBack();
            }

            if (data.data && data.data.response) {

                // unmark last first unread message to avoid auto scroll to this new one
                if (this.firstUnread) {
                    this.firstUnread.firstNew = null;
                    this.firstUnread = null;
                    this.unreadCount = 0;
                }

                if (this.selectedChatGroup) {
                    // prepare chat group messages
                    this.prepareChatGroup(data.data.response, true);

                } else {

                    // get last message ID
                    let lastId = 0;
                    if (this.conversation.messages.length) {
                        lastId = this.conversation.messages[this.conversation.messages.length - 1].id;
                    }

                    // push new messages into existing conversation
                    data.data.response.messages.forEach((message, i) => {

                        this.participantService.linkedItem = null;

                        if (lastId < message.id) {
                            if (message.system_message == 'LINKED_ITEM') {
                                if (data.data.response.messages[i + 1]) {
                                    data.data.response.messages[i + 1].linkedItem = JSON.parse(message.message);
                                    data.data.response.messages[i + 1].linkedItem.clickable = true;
                                }
                            } else {
                                this.conversation.messages.push(new ConversationMessage(message))
                                // move offset to count with those new message
                                this.offset++;
                            }
                        }
                    });

                }

                this.scrollChatToBottom();

                // TODO: reset messageValue
                // this.newMessage = new ConversationMessage;
                this.messageValue = '';
                this.submitting = false;
            }


        });

        return await modal.present();
    }

    /**
     * toggle detail visibility
     *
     */
    public toggleDetail() {
        this.detailVisible = !this.detailVisible;
        this.chatVisible = false;
    }

    /**
     * disable detail and chat visibility
     *
     */
    public disableSideboxes() {
        this.detailVisible = false;
        this.chatVisible = false;
    }

    /**
     * toggle chat visibility
     *
     */
    public toggleChat() {
        this.chatVisible = !this.chatVisible;
        this.unreadCount = 0;
    }

    /**
     * Toggle like
     *
     * @param participant
     */
    public toggleLike(attendee: Participant) {
        if (!this.likeLoading) {
            this.likeLoading = true;
            let subscription = this.participantService.toggleLike(this.user.selected_participant.id, attendee.id).subscribe(
                (success) => {
                    this.likeLoading = false;
                    attendee.liked = !attendee.liked;
                    this.overlayService.showSuccess(success.message);

                    this.appEvents.publish('attendee:changed', attendee);
                },
                (error) => {
                    this.likeLoading = false;
                    this.overlayService.showError(error.error.message);
                });
            this.plt.activeSubscriptions.push(subscription);
        }
    }

    /**
      * leave chat group
      *
      * @return void
      */
    public leaveChatGroup(chatGroup: ChatGroup): void {
        let subscription = this.chatGroupsService.leaveChatGroup(chatGroup.id).subscribe(
            () => {
                //
                this.router.navigate([this.plt.defaultLink + '/messages'])
                // in tab design we will return user to page with chat group list
                if (this.plt.tabMenu) {
                    this.resetDetail();
                }
                this.appEvents.publish('messages:refresh:list', null, true);
            },
            (error) => {
                const data = error.error;
                this.overlayService.showError(data.message);
                this.checkDeletedFromGroup(data);
            });
        this.plt.activeSubscriptions.push(subscription);
    }

    /**
      * confirm of leaving chat group
      *
      * @return void
      */
    public async leaveChatGroupConfirm(chatGroup: ChatGroup) {
        this.overlayService.showConfirm(
            this.translate.instant('CHAT_GROUPS_LEAVE'),
            this.translate.instant('CHAT_GROUPS_LEAVE_HINT') + (this.selectedChatGroup.user_is_owner ? this.translate.instant('CHAT_GROUPS_LEAVE_HINT_ADMIN') : ""),
            () => { this.leaveChatGroup(chatGroup) },
            () => { },
            this.translate.instant('YES'),
            this.translate.instant('NO')
        );
    }

    /**
     * navigate app to list of attendees
     *
     * @return void
     */
    public goToAttendees() {
        this.router.navigate([this.plt.defaultLink + '/home/attendees/list']);
    }

    /**
     * save actual content from textbox as draft...
     *
     * @return void
     */
    private saveDrafts() {
        if (this.selectedParticipant) {
            this.conversationService.directMessageDrafts[this.selectedParticipant.id] = this.messageValue;
        }

        if (this.selectedChatGroup) {
            this.conversationService.groupMessageDrafts[this.selectedChatGroup.id] = this.messageValue;
        }
        // reset input
        this.messageValue = '';
    }

    /**
      * join chat group
      *
      * @return void
      */
    public joinChatGroup(id: number): void {
        let subscription = this.chatGroupsService.joinChatGroup(id).subscribe(
            (success) => {
                this.appEvents.publish('messages:refresh:list', id);
            },
            (error) => {
                const data = error.error;
                this.overlayService.showError(data.message);
            }
        );
        this.plt.activeSubscriptions.push(subscription);
    }

    /**
      * switched status of chat group details
      *
      * @return void
      */
    public chatGroupDetailOpened(opened: number) {
        this.chatGroupdetails.forEach(
            (chatGroupDetail, index) => {
                if (index != opened) {
                    this.chatGroupdetails[index] = false;
                } else {
                    this.chatGroupdetails[index] = true;
                }
            });
    }

    /**
      * open video call
      *
      * @return void
      */
    public openVideoCall(appointment?: Appointment) {

        // keep app awake
        this.plt.keepAwake();

        // in cordova native app we will open innAppBrowser with PWA
        if (this.plt.is('cordova')) {

            // reset call trigger to avoid opening call next time when opening chat detail
            this.openVideCallAfterInit = false;

            // NOTE[jg] now we will not use opening PWA, it should be working by default
            if (this.plt.is('ios') && false) {
                let link = this.env.pwa + this.routerExtService.currentUrl.replace('?call=true', '') + '?call=true&external=true&token=' + this.userToken;
                if (this.external) {
                    link += '&external=true'
                }

                // chat group
                if (appointment?.chat_group) {
                    let link = this.plt.defaultLink + '/messages/channel/' + appointment.chat_group.id + '?call=true';
                }

                this.plt.openInAppBrowser(link, '', false, false, '_system');
            } else {

                // if call to chat group we will redirect to this group
                if (this.selectedParticipant && appointment?.chat_group) {
                    let link = this.plt.defaultLink + '/messages/channel/' + appointment.chat_group.id + '?call=true';

                    this.router.navigate([link], {
                        replaceUrl: true,
                        queryParams: {
                            call: true
                        }
                    });
                }

                // check permission on cordova devices and ask for it if needed

                this.platform.ready().then(() => {

                    cordova.plugins.diagnostic.isMicrophoneAuthorized(
                        (authorized) => {
                            log('info', "App is " + (authorized ? "authorized" : "denied") + " access to the microphone");
                            if (!authorized) {
                                cordova.plugins.diagnostic.requestMicrophoneAuthorization(
                                    (status) => {
                                        if (status === cordova.plugins.diagnostic.permissionStatus.GRANTED) {
                                            this.startCall();
                                            log('info', "Microphone use is authorized");
                                            cordova.plugins.diagnostic.isCameraAuthorized(
                                                (authorized) => {
                                                    log('info', "App is " + (authorized ? "authorized" : "denied") + " access to the camera");
                                                    if (!authorized) {
                                                        cordova.plugins.diagnostic.requestCameraAuthorization(
                                                            (status) => {
                                                                log('info', "Authorization request for camera use was " + (status == cordova.plugins.diagnostic.permissionStatus.GRANTED ? "granted" : "denied"));
                                                            }, (error) => {
                                                                log('error', "The following error occurred: " + error);
                                                            }, false
                                                        );
                                                    }
                                                }, (error) => {
                                                    log('error', "The following error occurred: " + error);
                                                }, false
                                            );
                                        }

                                    }, (error) => {
                                        log('error', error);
                                    });
                            } else {
                                // usage is authorized, so we will start a call
                                this.startCall();
                            }
                        }, (error) => {
                            log('error', "The following error occurred: " + error);
                        });
                });
            }
        } else {
            if (appointment?.chat_group) {
                this.openVideCallAfterInit = true;
                this.router.navigate([this.plt.defaultLink + '/messages/channel/' + appointment.chat_group.id + '?call=true']);
                // this.appEvents.publish('messages:chatgroup', new ChatGroup(appointment.chat_group));
                // this.chatGroupSelectedHandler.call(new ChatGroup(appointment.chat_group))
                // this.selectedParticipant = null;
                // this.selectedChatGroup = new ChatGroup(appointment.chat_group);
            } else {
                this.startCall();
            }
        }
    }

    /**
      * start video call
      *
      * @return void
      */
    startCall() {
        let delay = 0;
        if (this.videoCallOpened) {
            this.videoCallOpened = false;
            delay = 500;
        }
        this.openVideCallAfterInit = false;
        // check if call is allowed for at least one of the side
        if ((this.selectedParticipant && this.selectedParticipant.id) || this.selectedChatGroup) {
            if (
                this.user &&
                (
                    this.selectedChatGroup
                    || this.event.can(this.user.selected_participant.group_id, 'allow_call')
                    || this.event.can(this.selectedParticipant.group_id, 'allow_call')
                )
            ) {

                setTimeout(() => {
                    if (this.plt.tabMenu) {
                        this.plt.hideBottomMenu = true;
                    }

                    this.videoCallOpened = true;
                    this.detailVisible = false;
                    this.chatVisible = false;
                    this.plt.hideSidebar = true;
                    // add attribute to URL
                    let link = this.routerExtService.currentUrl.replace('?call=true', '') + '?call=true';

                    // if call to chat group we will redirect to this group
                    if (this.selectedChatGroup) {
                        link = this.plt.defaultLink + '/messages/channel/' + this.selectedChatGroup.id + '?call=true';
                    }

                    // add also external param if set
                    if (this.external) {
                        link += '&external=true'
                    }
                    this.routerExtService.softNavigate(link);
                    // hide notificatinos
                    // NOTE[jg] - use some enum for notificaiton type
                    if (this.selectedParticipant) {
                        if (this.overlayService.notifications['call' + this.selectedParticipant.id]) {
                            this.overlayService.notifications['call' + this.selectedParticipant.id].dismiss();
                            this.overlayService.notifications['call' + this.selectedParticipant.id] = null;
                        }
                    }
                }, delay);

            } else {
                // remove call link if call is not allowed
                this.routerExtService.softNavigate(this.routerExtService.currentUrl.replace('?call=true', ''));
            }
        } else {
            // wait until participant is loaded and then init call
            this.openVideCallAfterInit = true;
        }
    }

    /**
      * open video call
      *
      * @return void
      */
    public closeVideoCall() {

        this.plt.allowSleepAgain();

        if (this.external) {
            // close window
            setTimeout(() => {
                window.close();
            }, 200);
            // make a redirect
            setTimeout(() => {
                window.open(this.env.deeplink + '/app-open', '_blank');
            }, 100);
        }
        this.videoCallOpened = false;
        this.plt.hideSidebar = false;
        this.routerExtService.softNavigate(this.routerExtService.currentUrl.replace('?call=true', ''));
        // scroll chat to last message
        setTimeout(() => {
            this.scrollChatToBottom();
        }, 250);
    }

    /**
     * open modal with chat group craeting form
     */
    async editChatGroup() {

        this.routerExtService.softNavigate();

        const modal = await this.modalController.create({
            component: ChagGroupsFormComponent,
            componentProps: {
                chatGroup: new ChatGroup(this.selectedChatGroup)
            },
            cssClass: 'chat-group-form'
        });

        modal.onWillDismiss().then((data) => {

            // refresh list with chat groups
            if (data.data?.chatGroupId) {
                this.appEvents.publish('messages:refresh:list', data.data.chatGroupId);
            }

            if (data.data?.action == 'close' || data.role == 'backdrop') {
                this.routerExtService.softBack();
            }

        });

        return await modal.present();
    }

    /**
     * verify if link to linked item should be clickable
     *
     * @return modal
     */
    public async verifyLinkedItem(linkedItem) {
        // if marketplaces are allowed in this event/community
        if (this.event.marketplaces.length > 0) {
            let subscription = this.marketplaceService.getPost(+linkedItem.marketplace_id, +linkedItem.id).subscribe(
                () => {
                    linkedItem.clickable = true;
                },
                () => {
                    linkedItem.clickable = false;
                });
            this.plt.activeSubscriptions.push(subscription);
        }
    }

    /**
     * load marketplace post detail and open modal if possible
     *
     * @return modal
     */
    public async showMarketplacePostDetailFromLinked(linkedItem) {
        if (linkedItem.clickable) {
            let subscription = this.marketplaceService.getPost(+linkedItem.marketplace_id, +linkedItem.id).subscribe(
                (marketplacePost: MarketplacePost) => {
                    this.openMarketplacePostDetail(new MarketplacePost(marketplacePost, this.userLang));
                });
            this.plt.activeSubscriptions.push(subscription);
        }
    }

    /**
     * scroll textarea into view
     *
     * @return void
     */
    public textareaChanged() {
        setTimeout(() => {
            document.getElementById('messages-textarea').scrollIntoView({ block: 'end' });
        }, 300);
    }

    /**
     * Update app view for videocalls
     *
     * @return void
     */
    public openVideoCallAttendees() {
        if (!this.detailVisible) {
            this.toggleDetail();
        }

        // open last tab of chat group detals
        this.chatGroupDetailOpened(4);
    }

    /**
     * @return void
     */
    public goToDashboard() {

        if (this.plt.tabMenu) {
            this.plt.hideBottomMenu = this.pltBackup.hideBottomMenu;
        }

        this.plt.hideSidebar = this.pltBackup.hideSidebar;

        this.selectedBroadcast = false;
        this.selectedChatGroup = null;
        this.selectedParticipant = null;
        if (this.chatGroupList) {
            this.chatGroupList.resetDetail();
        }
        this.appEvents.publish('messages:refresh:list', null, true);
        this.router.navigate([this.plt.defaultLink + '/home/info/matching']);
    }

    /**
     * on destroy
     *
     * @return void
     */
    ngOnDestroy(): void {

        this.plt.allowSleepAgain();

        // store message draft is some
        this.saveDrafts();

        // unsubscribe all liseners fot this page
        if (this.eventChangeHandler) {
            this.appEvents.unsubscribe('event:changed', this.eventChangeHandler);
        }
        if (this.refreshHandler) {
            this.appEvents.unsubscribe('refresh:messages', this.refreshHandler);
        }
        if (this.participantSelectedHandler) {
            this.appEvents.unsubscribe('messages:participant', this.participantSelectedHandler);
        }
        if (this.broadcastSelectedHandler) {
            this.appEvents.unsubscribe('messages:broadcast:', this.broadcastSelectedHandler);
        }
        if (this.chatGroupSelectedHandler) {
            this.appEvents.unsubscribe('messages:chatgroup', this.chatGroupSelectedHandler);
        }
        if (this.refreshHandlerLive) {
            this.appEvents.unsubscribe('refresh:messages:live', this.refreshHandlerLive);
        }
        if (this.chatGroupAddHandler) {
            this.appEvents.unsubscribe('messages:chatgroup:add', this.chatGroupAddHandler);
        }
        if (this.openCallHandler) {
            this.appEvents.unsubscribe('messages:call', this.openCallHandler);
        }
        if (this.chatGroupAdminsChangeHandler) {
            this.appEvents.unsubscribe('chatgroup:admins:changed', this.chatGroupAdminsChangeHandler);
        }
        if (this.callAttendeesHandler) {
            this.appEvents.unsubscribe('call:attendees', this.callAttendeesHandler);
        }
        if (this.callAttendeesOpenHandler) {
            this.appEvents.unsubscribe('call:attendees:open', this.callAttendeesOpenHandler);
        }

        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
        this.overlayService.closeModals();
    }
}
